import { useEffect, useRef } from 'react';
import { type RouteContext } from 'react-resource-router';

/* eslint-disable no-restricted-imports */
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { useOneTrustContext } from '@app/onetrust';

/**
 * Central consent-aware UFO watcher hook.
 *
 * This hook should be called ONCE from a central location (App.tsx) rather
 * than from individual page components. It tracks:
 *   - traceUFOPageLoad: on initial app load
 *   - traceUFOTransition: on subsequent route changes (navigating between pages)
 *
 * The experience name is derived from the matched route's name, keeping UFO
 * experience keys consistent with the route definitions in Routes.ts.
 *
 * Gated behind OneTrust Performance Cookies consent (category 2).
 *
 * NOTE: traceUFOPageLoad and traceUFOTransition should not be called directly
 * from page components — only from this central watcher. Individual pages
 * should use <UFOSegment> and <UFOLoadHold> for their interaction contexts.
 *
 * @param routeContext - The router state from useRouter() (inside a <Router>)
 *
 * @example
 * // In AppContents (inside <Router>):
 * const [routerState] = useRouter();
 * useReactUFOWatcher(routerState);
 */
export const useReactUFOWatcher = (routeContext: RouteContext): void => {
  const isInitialLoad = useRef(true);
  const { analyticsOptOut } = useOneTrustContext();

  // useEffect ensures trace calls only fire when the route actually changes,
  // not on every re-render. Without this, each re-render would start a new
  // UFO interaction, aborting the previous one before data finishes loading.
  useEffect(() => {
    if (analyticsOptOut || !routeContext.route) {
      return;
    }

    const experienceName = routeContext.route.name.toLowerCase();

    if (isInitialLoad.current) {
      traceUFOPageLoad(experienceName);
      isInitialLoad.current = false;
    } else {
      traceUFOTransition(experienceName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeContext.route, analyticsOptOut]);
};
